import { useCallback, useState } from "react";
import type { AvailableLocation } from "../types/featureAccess";
import { notifyError } from "../utils/notify";
import { useApp } from "../context/AppContext";
import { useSalon } from "../context/SalonContext";
import {
  clearActingLocationUuid,
  setActingLocationUuid,
} from "../utils/actingLocation";

/**
 * Multi-location switcher (PR A / #399).
 *
 * Shared by both entry points: the first-sign-in picker and the manual NavBar
 * switch. On selecting a location it stores it as THIS tab's location
 * (sent on every request as `X-Business-Location-Id` — utils/actingLocation),
 * remembers it as the landing location for new tabs / sign-ins
 * (`PATCH /staff-users/me { last_used_salon_id }`), and then does a FULL page
 * reload. Other open tabs and devices keep their own location.
 *
 * The full reload is intentional (design Q11): it guarantees permissions,
 * feature flags, and Pusher subscriptions all re-initialize for the new salon,
 * and — because the backend scopes every request by the tab's header — every
 * PHI fetch re-scopes to the new salon by construction. Do NOT try a soft
 * in-place swap.
 *
 * `isSwitching` is exposed so the caller can render a full-screen spinner
 * during the brief PATCH→reload window.
 */
export function useLocationSwitcher() {
  const { patchStaffUsersMe } = useApp();
  // SalonContext is untyped JS; cast as any (the established consumer pattern).
  const {
    selectedSalonId,
    setSelectedSalonId,
    closeLocationPicker,
    availableLocations,
  } = useSalon() as any;
  const [isSwitching, setIsSwitching] = useState(false);

  // Resolves true when the switch succeeded (the PATCH landed and the reload
  // is in flight, or the pick was a no-op) and false when the PATCH failed —
  // so callers that gate UI on the switch (e.g. useOwnerOnboardingRedirect's
  // "redirecting" overlay) can stand down instead of spinning forever.
  const switchLocation = useCallback(
    async (salonId: number): Promise<boolean> => {
      // Picking the already-selected location is a no-op — just close the
      // picker (relevant only in the manual-switch context; on first sign-in
      // selectedSalonId is null so every pick is a real switch).
      if (salonId === selectedSalonId) {
        closeLocationPicker();
        return true;
      }

      setIsSwitching(true);
      // Optimistic highlight for the brief window before the reload.
      setSelectedSalonId(salonId);
      try {
        await patchStaffUsersMe(salonId);
        const picked = (availableLocations ?? []).find(
          (location: AvailableLocation) => location.salon_id === salonId,
        );
        if (picked) {
          setActingLocationUuid(picked.salon_uuid);
        } else {
          // Unknown uuid: drop the old one so the reload lands on the
          // location just PATCHed (the server's landing default) instead of
          // silently staying put.
          clearActingLocationUuid();
        }
        // Full reload — re-inits permissions, feature flags, Pusher, and
        // re-scopes all reads to the new salon server-side.
        window.location.reload();
        return true;
      } catch (error) {
        // Reload never happens on failure; surface it and let the user retry.
        setIsSwitching(false);
        // Clear the optimistic OVERRIDE (null restores the server-derived
        // selection) rather than pinning the current derived value, which
        // would mask later /access changes to selected_salon_id.
        setSelectedSalonId(null);
        notifyError(
          "We couldn't switch to that location. Please try again in a moment.",
          { title: "Couldn't switch location" },
        );
        return false;
      }
    },
    [
      patchStaffUsersMe,
      selectedSalonId,
      setSelectedSalonId,
      closeLocationPicker,
      availableLocations,
    ],
  );

  return { switchLocation, isSwitching };
}

export default useLocationSwitcher;
